import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map, Observable, of, pipe } from 'rxjs';
import { environment } from 'src/environments/environment';
import { Order, QueryOptions, Where } from '../helpers/QueryOptions';
import { Pagination } from '../models/Pagination';
import { Product } from '../models/Product';
import { ProductQuestion } from '../models/ProductQuestion';
import { ServerResponse } from '../models/ServerResponse';
import { ProductFilter } from '../modules/shared/classes/ProductFilter';
import { TokenModel } from '../modules/auth/models/TokenModel';
import { QuestionsGroupedByProduct } from '../modules/my-sales/models/QuestionsGroupedByProduct';
import { AsyncPaginatorData } from '../modules/shared/classes/AsyncPaginatorData';
import { ProductFormFields } from '../modules/product-form/classes/ProductFormFields';
import { ProductSearchFilters } from '../modules/shared/classes/ProductSearchFilters';
import { eProductFilterType } from '../modules/shared/classes/eProductFilterItemType';
import { eProductFilterGroupType } from '../modules/shared/classes/eProductFilterGroupType';
import { eProductStatus } from '../models/eProductStatus';

@Injectable({
	providedIn: 'root'
})
export class ProductService {

	readonly API_PRODUCTS_URL: string;

	constructor(private http: HttpClient) {
		this.API_PRODUCTS_URL = environment.API_URL + "/product";
	}

	//#region Formulario de producto
	/**
	 * Se usa tanto para publicar como para crear como borrador. La única diferencia es el status que se le paas en pff
	 * @returns Producto creado
	 */
	create(pff: ProductFormFields) : Observable<Product> {
		let route = this.API_PRODUCTS_URL + "/create";
		return this.http.post<ServerResponse<Product>>(route, pff).pipe(
			map((sr: ServerResponse<Product>) => {
				return sr.response!
			})
		);
	}
	/**
	 * GET de producto verificado por el store_id del que consulta, para llenar el formulario de edición de un producto en ProductFormComponent
	 */
	getForEdition(product_id:number) : Observable<Product> {
		let route = this.API_PRODUCTS_URL + "/forEdit/" + product_id;
		return this.http.get<ServerResponse<Product>>(route).pipe(
			map((r: ServerResponse<Product>) => {
				return r.response!;
			})
		);
	}
	/**
	 * Para modificar un producto ya creado
	 */
	edit(product_id:number, pff:ProductFormFields) : Observable<Product> {
		let route = this.API_PRODUCTS_URL + "/"+ product_id +"/update";
		return this.http.post<ServerResponse<Product>>(route, pff).pipe(
			map((r: ServerResponse<Product>) => {
				return r.response!;
			})
		);
	}
	setStatus(product_id:number, status:eProductStatus.DRAFT|eProductStatus.ACTIVE) : Observable<Product> {
		let route = this.API_PRODUCTS_URL + "/"+ product_id +"/update";
		return this.http.post<ServerResponse<Product>>(route, {status: status}).pipe(
			map((r: ServerResponse<Product>) => {
				return r.response!;
			})
		);

	}
	//#endregion

	//#region Vista de producto
	getById(id: number) : Observable<Product> {
		let route = this.API_PRODUCTS_URL + "/fetch/" + id;
		return this.http.get<ServerResponse<Product>>(route).pipe(
			map((r: ServerResponse<Product>) => {
				return r.response!;
			})
		);
	}
	getQuestions(product_id:number, apd:AsyncPaginatorData): Observable<Pagination<ProductQuestion>> {
		let route = this.API_PRODUCTS_URL + "/questions/" + product_id;

		let qo = apd.toQueryOptions();

		return this.http.post<ServerResponse<Pagination<ProductQuestion>>>(route, qo).pipe(
			map((sr:ServerResponse<Pagination<ProductQuestion>>) => {
				return sr.response!;
			})
		);
	}
	toggleFavorite(product_id: number): Observable<ServerResponse<true>> {
		let route = this.API_PRODUCTS_URL + "/toggle-favorite/" + product_id;
		return this.http.get<ServerResponse<true>>(route);		// No me interesa el return. Siempre viene true
	}
	saveClick(product_id: number, token?: TokenModel): Observable<ServerResponse<Product>> {
		let route = this.API_PRODUCTS_URL + '/click';
		return this.http.post<ServerResponse<Product>>(route, { id: product_id });
	}
	//#endregion

	//#region Home
	getLastVisited() : Observable<Product[]> {
		let route = environment.API_URL + "/last-visit/list";

		let qo = new QueryOptions();
		qo.withs = ["productDiscount"];
		return this.http.post<ServerResponse<Product[]>>(route, qo).pipe(
			map((sr: ServerResponse<Product[]>) => {
				return sr.response!;
			})
		);
	}
	//#endregion

	//#region Preguntas
	addQuestion(product: Product, question: string): Observable<ProductQuestion> {
		let route = this.API_PRODUCTS_URL + "/add-question/" + product.id;

		return this.http.post<ServerResponse<ProductQuestion>>(route, { comment: question, store_id: product.store_id }).pipe(
			map((sr: ServerResponse<ProductQuestion>) => {
				return sr.response!;
			})
		);
	}
	answerQuestion(question_id: number, answer: string): Observable<ProductQuestion> {
		let route = environment.API_URL + "/product_question/answer";

		return this.http.post<ServerResponse<ProductQuestion>>(route, { id: question_id, answer: answer }).pipe(
			map((sr: ServerResponse<ProductQuestion>) => {
				return sr.response!;
			})
		);
	}
	//#endregion

	//#region Búsqueda
	getFilters(pfi: ProductFilter[] = [], searchInput?: string, store_id?:number): Observable<ProductSearchFilters> {
		let route = this.API_PRODUCTS_URL + "/filters";
		let body : any = {
			current: pfi,
		}

		// Con búsuqeda?		
		if (searchInput) {
			body.q = searchInput;
		}
		
		// De un store?
		if (store_id) {
			if (!body.current.find((f:ProductFilter)=> f.group_id == eProductFilterGroupType.STORES))
				body.current.push(new ProductFilter(store_id, eProductFilterGroupType.STORES, eProductFilterType.EQUAL));
		}

		return this.http.post<ServerResponse<ProductSearchFilters>>(route, body).pipe(
			map((sr: ServerResponse<ProductSearchFilters>) => {
				return sr.response!;
			})
		)
	}
	searchProducts(apd:AsyncPaginatorData, productFilters?: ProductFilter[], searchInput?: string, store_id?:number): Observable<Pagination<Product>> {
		let route = this.API_PRODUCTS_URL + "/search";

		// En esta ruta se para el QueryOptions a un objeto any porque hay que agregarle atributos extra
		let qo = new QueryOptions();
		qo.withs = ["productDiscount"];
		qo.orders = [new Order("created_at", "desc")];

		// Paginado
		qo = apd.toQueryOptions(qo);

		// Con filtros?
		let body: any = qo;
		if (productFilters) {
			body.current = productFilters;
		}

		// Con búsuqeda?		
		if (searchInput) {
			body.q = searchInput
		}

		// De un store?
		if (store_id)
			body.wheres = [new Where("equal", "store_id", store_id)];

		return this.http.post<ServerResponse<Pagination<Product>>>(route, body).pipe(
			map((sr: ServerResponse<Pagination<Product>>) => {
				return sr.response!;
			})
		)
	}
	//#endregion

	//#region Mis compras
	getQuestionsMadeByMe(apd:AsyncPaginatorData,/* qff:QuestionFilterFields*/) : Observable<Pagination<QuestionsGroupedByProduct>> {
		let route = this.API_PRODUCTS_URL + "/questions/search";
		
		let qo = apd.toQueryOptions();
		//qo = qff.to_Not_QueryOptions(qo);

		return this.http.post<ServerResponse<Pagination<QuestionsGroupedByProduct>>>(route, qo).pipe(
			map((sr:ServerResponse<Pagination<QuestionsGroupedByProduct>>)=> {
				return sr.response!
			})
		)
	}
	getMyFavorites(apd:AsyncPaginatorData, searchInput:string|null) : Observable<Pagination<Product>> {
		let route = environment.API_URL + "/product_favorite/list";
		let qo = apd.toQueryOptions();
		if (searchInput)
			qo.search = searchInput;

		return this.http.post<ServerResponse<Pagination<Product>>>(route, qo).pipe(
			map((sr:ServerResponse<Pagination<Product>>)=> {
				return sr.response!;
			})
		);
	}
	//#endregion

	//#region Home y 404
	get4Random() : Observable<any> {
		let route = this.API_PRODUCTS_URL + "/list/random";
		return this.http.get<ServerResponse<any>>(route).pipe(
			map((sr:ServerResponse<any>)=> {
				return sr.response!;
			})
		);
	}
	//#endregion
}
